/** @format */

import {useState, useEffect, useContext, useRef} from 'react';
import SuperAgent from 'superagent';
import update from 'immutability-helper';
import QuoteContext from 'frontier/quoter/components/quote_context';
import {REGEX_DESIGN_FILENAME} from 'frontier-constants';

export function useDesignHandler(props) {
    const context = useContext(QuoteContext);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSwapping, setIsSwapping] = useState(false);
    const [designOrder, setDesignOrder] = useState({qty: props.qty});

    // Place AbortController and timeoutID in ref to persist between renders.
    const abortController = useRef(new AbortController());
    const updateTimeoutID = useRef(null);

    useEffect(() => {
        if (props.qty !== designOrder.qty) {
            setDesignOrder(prevState => update(prevState, {qty: {$set: props.qty}}));
        }
    }, [props.qty]);

    useEffect(() => {
        handleUpdateRequest();
    }, [designOrder]);

    const handleSwap = async () => {
        setIsSwapping(true);
        await SuperAgent.get('/account/designs/swap/' + props.design_id).set(
            'accept',
            'application/json'
        );
        props.onDesignUpdate();
        setIsSwapping(false);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        await SuperAgent.delete('/account/designs/delete/' + props.id).set(
            'accept',
            'application/json'
        );
        props.onDelete();
    };

    const handleUpdateRequest = () => {
        // Clear previous timeout and abort previous request if any
        if (updateTimeoutID.current) {
            clearTimeout(updateTimeoutID.current);
        }

        // Abort previous fetch request if it's still ongoing
        abortController.current.abort();

        // Create new AbortController instance for the new request
        abortController.current = new AbortController();

        updateTimeoutID.current = setTimeout(async () => {
            try {
                const endpoint = `/orders/quote-update-designs-order/${props.id}`;
                const response = await fetch(endpoint, {
                    signal: abortController.current.signal,
                    method: 'POST',
                    body: JSON.stringify(designOrder),
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                });

                const {designQty, totalQty} = await response.json();
                context.handleUpdate({
                    order: {design_qty: {$set: designQty}, total_qty: {$set: totalQty}}
                });
                props.onDesignUpdate();
            } catch (e) {
                // Request was likely aborted
                if (e.name !== 'AbortError') {
                    console.error('Update request failed', e);
                }
            }
        }, 500);
    };

    const handleChangeQty = (designId, qty) => {
        setDesignOrder(prevState => update(prevState, {qty: {$set: qty}}));
    };

    const handleUpdateFilename = newFilename => {
        setDesignOrder(prevState => {
            const filenameExtension = prevState.design.customer_filename.split('.').pop();
            const cleanValue =
                newFilename.replace(REGEX_DESIGN_FILENAME, '') + '.' + filenameExtension;
            return update(prevState, {design: {customer_filename: {$set: cleanValue}}});
        });
    };

    return {
        isDeleting,
        isSwapping,
        designOrder,
        handleSwap,
        handleDelete,
        handleChangeQty,
        handleUpdateFilename
    };
}
